/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { withAnalytics } from '@veneer/analytics';
import { Card, useDirection, useMediaQuery } from '@veneer/core';
import React, { memo, useCallback, useMemo } from 'react';
import Button from '../button';
import './styles.scss';
import { CTACardProps } from './types';
import { breakpoints } from '../../base';
import { generateID } from '../../base/helper';

/**
 * UI Toolkit CTA Card
 * @public
 */
const TCTACard: React.FC<CTACardProps> = ({
  id = generateID('clientos-ui-toolkit-cta-card'),
  title,
  descriptor,
  primaryBtn,
  trailingImg,
  onCardClick,
  body,
  type,
  paddingBodyHorizontal = true,
  paddingBodyBottom = true,
  paddingBodyTop = true
}) => {
  const rtl = useDirection().isRTL;
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.md})`);

  const primaryBtnClicked = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      primaryBtn.onClick && primaryBtn.onClick(event);
    },
    [primaryBtn]
  );

  const bodyClasses = useMemo(() => {
    return [
      'clientos-ui-toolkit-cta-card__body',
      paddingBodyHorizontal && 'clientos-ui-toolkit-cta-card__body--padding',
      paddingBodyBottom && 'clientos-ui-toolkit-cta-card__body--paddingBottom',
      paddingBodyTop && 'clientos-ui-toolkit-cta-card__body--paddingTop'
    ]
      .filter(Boolean)
      .join(' ');
  }, [paddingBodyHorizontal, paddingBodyBottom, paddingBodyTop]);

  return (
    <Card
      appearance="outlined"
      className={`clientos-ui-toolkit-cta-card ${type === 'negative' ? 'negative' : ''} ${body ? 'has-body' : ''}`}
      onClick={onCardClick}
      id={id}
      content={
        <div
          className="clientos-ui-toolkit-cta-card__content"
          id={`${id}-content`}
        >
          <div
            className="clientos-ui-toolkit-cta-card__header"
            id={`${id}__cta-card-header`}
          >
            <div
              className="clientos-ui-toolkit-cta-card__container"
              id={`${id}__cta-card-container`}
            >
              <h2
                className={`clientos-ui-toolkit-cta-card__title ${isDesktop ? 'title-small--medium' : 'title-medium'}`}
                id={`${id}__cta-card-title`}
                aria-label={title}
                role="text"
              >
                {title}
              </h2>
              <div
                className="clientos-ui-toolkit-cta-card__descriptor body-large"
                id={`${id}__cta-card-descriptor`}
                aria-label={descriptor}
                role="text"
              >
                {descriptor}
              </div>
              {primaryBtn && (
                <div
                  className="clientos-ui-toolkit-cta-card__primary-btn"
                  id={`${id}__cta-card-primary-btn`}
                >
                  <Button
                    {...primaryBtn}
                    icon={primaryBtn.icon}
                    onClick={primaryBtnClicked}
                    id={`${id}__cta-card-button`}
                  >
                    {primaryBtn.label}
                  </Button>
                </div>
              )}
            </div>
            {trailingImg && (
              <div
                className={`clientos-ui-toolkit-cta-card__background ${rtl ? 'clientos-ui-toolkit-cta-card__background-rtl' : ''}`}
                id={`${id}__cta-card-background`}
              >
                <img
                  className="clientos-ui-toolkit-cta-card__background-img"
                  src={trailingImg}
                  alt="CTA Card background image"
                  id={`${id}__cta-card-background-image`}
                />
              </div>
            )}
          </div>
          <div
            className={bodyClasses}
            id={`${id}__cta-card-body`}
          >
            {body}
          </div>
        </div>
      }
    />
  );
};

export const CTACardAnalytics = withAnalytics(TCTACard);
export default memo(TCTACard);
